// ./src/components/features/CreateEventWizard/components/WizardListSection.tsx
//
// The body of a wizard step that ADDS things to the event (sign-up items, polls, games): a heading with Add, the plan
// notice, one-tap starters, and the list — in the four states every list owes, in order:
//   not read yet → skeleton; the read FAILED → the error with a retry (never "none yet", which reads as "your data is
//   gone"); none → the empty line; else the rows.
// ⚠ One component so those states cannot drift between steps; the step supplies its rows (`WizardItemRow`) and its
//   own drawer, which is always the entity's REAL editor.

import React from 'react';
import { Stack, Group, Text, Button, Skeleton } from '@mantine/core';
import { OpResult } from '@sdflc/api-helpers';
import { useTranslation } from 'react-i18next';

import { AppIcon, ErrorAlert, PlanFeatureNotice } from '@/components/generic';
import type { UseCreateGateReturn } from '@/hooks';

export interface WizardStarter {
  key: string;
  label: string;
  icon: string;
  onClick: () => void;
}

export interface WizardListSectionProps {
  title: string;
  addLabel: string;
  onAdd: () => void;
  /**
   * The create gate for what this step adds — only a NEW row is refused, out loud.
   * ⚠ REQUIRED, and `null` only where adding is not plan-gated at all (guests): an optional prop would let a step that
   * IS gated forget it and compile.
   */
  gate: UseCreateGateReturn | null;
  /** What the plan notice says the host cannot add ("another game"). */
  gateWhat: string;
  disabled?: boolean;
  starters?: WizardStarter[];
  /** ⚠ True once the FIRST read has come back — failed or not. */
  isLoaded: boolean;
  readError: OpResult | null;
  onRetry: () => void;
  errorTitle: string;
  /** For the error report. */
  operation: string;
  emptyText: string;
  isEmpty: boolean;
  children: React.ReactNode;
}

export const WizardListSection: React.FC<WizardListSectionProps> = ({
  title,
  addLabel,
  onAdd,
  gate,
  gateWhat,
  disabled = false,
  starters = [],
  isLoaded,
  readError,
  onRetry,
  errorTitle,
  operation,
  emptyText,
  isEmpty,
  children,
}) => {
  const { t } = useTranslation(['common']);
  const canCreate = gate ? gate.canCreate : true;

  const renderList = () => {
    if (!isLoaded) {
      return (
        <Stack gap="xs">
          <Skeleton height={60} radius="md" />
          <Skeleton height={60} radius="md" />
        </Stack>
      );
    }

    if (readError) {
      return (
        <Stack gap="sm">
          <ErrorAlert error={readError} title={errorTitle} page="Create event" operation={operation} />
          <Group>
            <Button variant="light" size="md" onClick={onRetry} leftSection={<AppIcon iconName="Refresh" size={16} />}>
              {t('common:retry', { defaultValue: 'Try again' })}
            </Button>
          </Group>
        </Stack>
      );
    }

    if (isEmpty) {
      return (
        <Text size="md" c="dimmed">
          {emptyText}
        </Text>
      );
    }

    return <Stack gap="xs">{children}</Stack>;
  };

  return (
    <Stack gap="sm">
      {gate ? <PlanFeatureNotice verdict={gate.verdict} onUpgrade={gate.goToUpgrade} what={gateWhat} gate="add" /> : null}
      <Group justify="space-between" wrap="nowrap">
        <Text fw={600} size="md">
          {title}
        </Text>
        <Button
          variant="light"
          size="md"
          leftSection={<AppIcon iconName="Plus" size={16} />}
          disabled={!canCreate || disabled}
          onClick={onAdd}
        >
          {addLabel}
        </Button>
      </Group>
      {isLoaded && !readError && starters.length > 0 ? (
        <Group gap="xs">
          {starters.map((s) => (
            <Button
              key={s.key}
              variant="default"
              size="md"
              leftSection={<AppIcon iconName={s.icon} size={16} />}
              disabled={!canCreate || disabled}
              onClick={s.onClick}
            >
              {s.label}
            </Button>
          ))}
        </Group>
      ) : null}
      {renderList()}
    </Stack>
  );
};
